/**
 * Icebreaker-grade pack. Same shape for the model seed and the client card.
 * Claims are sourced. The email domain stays the addressed company.
 */
import {
  CONSULTANT_VOICE_RULE,
  FIRST_TURN_HYPOTHESIS_OPENING,
  GENERIC_LIVE_IMPROV_MARKER,
  GREETING_HEDGE_MARKER,
  GROUNDED_PACK_ANSWER_RULE,
  GROUNDED_PACK_RESEARCH_KEY,
  ICEBREAKER_RULE,
} from '../contracts.js'
import { spokenSourceCount } from '../../lib/spoken-sources.js'
import { consultingLineLocale, formatResearchAckOpening } from '../agents/utils/working-hypothesis.js'
import { hasEmergencySiteSignal } from './grounded-research-claims.js'
import { brregEntityUrl, brregRollerUrl } from './registry-lookup.js'
import { companyNameMatchesDomain, isOwnSiteDomain, isOwnSiteUri } from './grounding-url.js'
import {
  pickBoundCompanyName,
  resolvePersonProfileUrl,
  type IdentityBoundCitation,
} from '../../lib/identity-bound-sources.js'

export interface GroundedSource {
  url: string
  title?: string
  snippet?: string
  query?: string
  publishedAt: string | null
  retrievedAt: string
}

export interface SourcedClaim {
  claim: string
  sourceUrl: string
  publishedAt?: string | null
}

export interface GroundedPackPerson {
  fullName: string
  role: string | null
  profileUrl: string | null
  currentCompany: string | null
}

export interface GroundedPackCompany {
  name: string
  domain: string
  website: string | null
  industry: string | null
  summary: string | null
  orgNumber: string | null
  country: string | null
}

export interface AiAutomationAngle {
  /** Research-framed issue. Not a personal bottleneck. */
  issue: string
  /** One automation example. */
  example: string
  /** Invite the visitor to steer. */
  invite: string
  sourceUrl: string | null
}

export interface GroundedPackV1 {
  version: 1
  person: GroundedPackPerson
  company: GroundedPackCompany
  roleSignals: SourcedClaim[]
  competitors: SourcedClaim[]
  pressurePoints: SourcedClaim[]
  aiAutomationAngle: AiAutomationAngle | null
  sources: GroundedSource[]
}

export interface GroundedSearchPass {
  query: string
  answer: string
  citations: IdentityBoundCitation[]
}

export interface BuildGroundedPackInput {
  email: string
  companyLabel: string
  fullName: string
  domain: string
  website?: string | null
  industry?: string | null
  summary?: string | null
  orgNumber?: string | null
  country?: string | null
  role?: string | null
  currentCompany?: string | null
  profileUrl?: string | null
  searchPasses?: GroundedSearchPass[]
  pageText?: string | null
  holders?: Array<{ name: string; role: string }>
  modelCompetitors?: string[]
  /** True only when this org number came back from the registry HTTP lookup. */
  registryRetrieved?: boolean
  retrievedAt?: string
}

const COMPETITOR_LINE =
  /(?:competitors?|konkurrent(?:er|en)?|alternatives? to)\s*[:—-]?\s*([^.!\n]{3,160})/i

const ROLE_WORD =
  /\b(daglig leder|styreleder|founder|co-founder|ceo|cto|owner|eier|rørlegger|plumber)\b/i

export function selectExecutedResearchQueries(queries: string[]): string[] {
  const linkedIn = queries.find(query => /site:linkedin\.com\/in/i.test(query))
  const proff = queries.find(query => /proff\.no|roller i norsk næringsliv/i.test(query))
  const primary = queries.find(query => query !== linkedIn && query !== proff)
  const seen = new Set<string>()
  const picked: string[] = []
  for (const query of [linkedIn, proff, primary]) {
    const trimmed = query?.replace(/\s+/g, ' ').trim()
    if (!trimmed || seen.has(trimmed)) continue
    seen.add(trimmed)
    picked.push(trimmed)
  }
  return picked.slice(0, 3)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

export function readGroundedPack(researchContext: unknown): GroundedPackV1 | null {
  const root = asRecord(researchContext)
  if (!root) return null
  const raw = asRecord(root[GROUNDED_PACK_RESEARCH_KEY]) || asRecord(root.groundedPack)
  if (!raw || raw.version !== 1) return null
  const person = asRecord(raw.person)
  const company = asRecord(raw.company)
  const fullName = asString(person?.fullName)
  const companyName = asString(company?.name)
  const domain = asString(company?.domain)
  if (!fullName || !companyName || !domain) return null
  return raw as unknown as GroundedPackV1
}

function clip(value: string, max = 220): string {
  const trimmed = value.replace(/\s+/g, ' ').trim()
  return trimmed.length > max ? `${trimmed.slice(0, max - 1).trimEnd()}…` : trimmed
}

function dedupeClaims(claims: SourcedClaim[]): SourcedClaim[] {
  const seen = new Set<string>()
  const out: SourcedClaim[] = []
  for (const claim of claims) {
    const key = `${claim.claim.toLowerCase()}|${claim.sourceUrl}`
    if (!claim.claim || !claim.sourceUrl || seen.has(key)) continue
    seen.add(key)
    out.push(claim)
  }
  return out
}

function sentenceWith(text: string, pattern: RegExp): string | null {
  const parts = text.split(/(?<=[.!?\n])\s+/)
  const hit = parts.find(part => pattern.test(part))
  return hit ? clip(hit) : null
}

export function buildGroundedPack(input: BuildGroundedPackInput): GroundedPackV1 {
  const retrievedAt = input.retrievedAt || new Date().toISOString()
  const emailDomain = input.domain.replace(/^www\./, '').toLowerCase()
  // Our own domain is not the client's site (issue 15, #231 0ad4c8fa).
  const domain = isOwnSiteDomain(emailDomain) ? '' : emailDomain
  const statedWebsite = input.website?.trim() || null
  const typedName = input.companyLabel.trim()
  const emailDomainIsTheirs = !typedName || companyNameMatchesDomain(typedName, emailDomain)
  const website = statedWebsite && !isOwnSiteUri(statedWebsite)
    ? statedWebsite
    : domain && emailDomainIsTheirs ? `https://${domain}` : null
  const companyName = pickBoundCompanyName(
    input.companyLabel,
    input.companyLabel,
    domain,
    website,
  )
  const passes = input.searchPasses || []
  const citations = passes.flatMap(row => row.citations || [])
  const profileUrl =
    resolvePersonProfileUrl(input.profileUrl, citations, input.fullName) ||
    resolvePersonProfileUrl(
      undefined,
      passes.map(row => ({
        uri: website || '',
        description: row.answer,
      })),
      input.fullName,
    ) ||
    null

  const sources: GroundedSource[] = []
  const seenUrls = new Set<string>()
  const pushSource = (url: string | null | undefined, title?: string, query?: string) => {
    const trimmed = url?.trim()
    if (!trimmed || seenUrls.has(trimmed)) return
    seenUrls.add(trimmed)
    sources.push({
      url: trimmed,
      ...(title ? { title } : {}),
      ...(query ? { query } : {}),
      publishedAt: null,
      retrievedAt,
    })
  }
  for (const pass of passes) {
    for (const citation of pass.citations || []) {
      const url = citation.uri?.trim()
      if (!url || seenUrls.has(url)) continue
      seenUrls.add(url)
      sources.push({
        url,
        ...(citation.title ? { title: citation.title } : {}),
        ...(citation.description ? { snippet: clip(citation.description, 320) } : {}),
        query: pass.query,
        publishedAt: citation.publishedAt ?? null,
        retrievedAt: citation.retrievedAt || retrievedAt,
      })
    }
  }
  if (input.pageText?.trim() && website) pushSource(website, companyName, 'homepage')
  const org = input.orgNumber?.replace(/\s/g, '') || null
  if (input.registryRetrieved && org) pushSource(brregEntityUrl(org), companyName, 'brreg')

  const corpus: Array<{ text: string; url: string; publishedAt: string | null }> = []
  if (input.pageText?.trim() && website) {
    corpus.push({ text: input.pageText, url: website, publishedAt: null })
  }
  for (const pass of passes) {
    const first = pass.citations.find(citation => citation.uri)?.uri
    if (pass.answer.trim() && first) {
      corpus.push({ text: pass.answer, url: first, publishedAt: null })
    }
    for (const citation of pass.citations) {
      const text = [citation.title, citation.description].filter(Boolean).join(' ')
      if (text && citation.uri) {
        corpus.push({
          text,
          url: citation.uri,
          publishedAt: citation.publishedAt ?? null,
        })
      }
    }
  }

  const roleSignals: SourcedClaim[] = []
  const norway = (input.country || '').toUpperCase() === 'NO' || domain.endsWith('.no') || Boolean(org)
  for (const holder of input.holders || []) {
    if (!holder.name.trim() || !holder.role.trim()) continue
    const sourceUrl = (org ? brregRollerUrl(org) : null)
      || sources.find(source => /brreg\.no|proff\.no/i.test(source.url))?.url
    if (!sourceUrl) continue
    pushSource(sourceUrl, 'Roller', 'brreg')
    const roller = norway ? ' Roller i norsk næringsliv.' : ''
    roleSignals.push({
      claim: clip(`${holder.name} — ${holder.role}.${roller}`),
      sourceUrl,
      publishedAt: null,
    })
  }
  for (const item of corpus) {
    if (!/proff\.no/i.test(item.url)) continue
    if (!/roller i norsk næringsliv/i.test(item.text) && !ROLE_WORD.test(item.text)) continue
    roleSignals.push({
      claim: clip(
        sentenceWith(item.text, /roller i norsk næringsliv|daglig leder|styreleder/i) ||
          `${companyName}: roller i norsk næringsliv`,
      ),
      sourceUrl: item.url,
      publishedAt: item.publishedAt,
    })
  }
  for (const item of corpus) {
    if (!/linkedin\.com\/in\//i.test(item.url) && !/linkedin\.com\/in\//i.test(item.text)) continue
    const line = sentenceWith(item.text, ROLE_WORD)
    if (!line) continue
    const sourceUrl = /linkedin\.com\/in\//i.test(item.url)
      ? item.url
      : profileUrl || item.url
    roleSignals.push({ claim: line, sourceUrl, publishedAt: item.publishedAt })
  }

  const competitors: SourcedClaim[] = []
  for (const item of corpus) {
    const match = item.text.match(COMPETITOR_LINE)
    const named = match?.[1]?.replace(/\s+/g, ' ').trim()
    if (!named) continue
    competitors.push({
      claim: clip(named),
      sourceUrl: item.url,
      publishedAt: item.publishedAt,
    })
  }
  const corpusBlob = corpus.map(item => item.text).join('\n').toLowerCase()
  for (const name of input.modelCompetitors || []) {
    const trimmed = name.replace(/\s+/g, ' ').trim()
    if (trimmed.length < 2) continue
    if (!corpusBlob.includes(trimmed.toLowerCase())) continue
    const hit = corpus.find(item => item.text.toLowerCase().includes(trimmed.toLowerCase()))
    if (!hit) continue
    competitors.push({
      claim: trimmed,
      sourceUrl: hit.url,
      publishedAt: hit.publishedAt,
    })
  }

  const pressurePoints: SourcedClaim[] = []
  for (const item of corpus) {
    if (!hasEmergencySiteSignal(item.text)) continue
    const issue =
      sentenceWith(item.text, /24|akutt|døgn|emergency|utrykning/i) ||
      'Public pages mention 24h akuttvakt / emergency callout coverage.'
    pressurePoints.push({
      claim: clip(issue),
      sourceUrl: item.url,
      publishedAt: item.publishedAt,
    })
  }

  const aiAutomationAngle = automationFromPressure(pressurePoints, consultingLineLocale('en'))

  return {
    version: 1,
    person: {
      fullName: input.fullName,
      role: input.role && input.role !== 'Unknown' ? input.role : null,
      profileUrl,
      currentCompany: input.currentCompany || null,
    },
    company: {
      name: companyName,
      domain,
      website,
      industry: input.industry || null,
      summary: input.summary || null,
      orgNumber: org || null,
      country: input.country || (domain.endsWith('.no') ? 'NO' : null),
    },
    roleSignals: dedupeClaims(roleSignals).slice(0, 6),
    competitors: dedupeClaims(competitors).slice(0, 4),
    pressurePoints: dedupeClaims(pressurePoints).slice(0, 4),
    aiAutomationAngle,
    sources,
  }
}

/** Keep a pack that already has source URLs when a later pass comes back empty. */
export function preferRicherGroundedPack(
  current: GroundedPackV1 | null | undefined,
  incoming: GroundedPackV1 | null | undefined,
): GroundedPackV1 | null {
  const count = (pack: GroundedPackV1 | null | undefined) =>
    pack?.sources?.filter(row => row.url?.trim()).length ?? 0
  const currentCount = count(current)
  const incomingCount = count(incoming)
  if (incoming && incomingCount > 0 && incomingCount >= currentCount) return incoming
  if (current && currentCount > 0 && incomingCount === 0) return current
  return incoming || current || null
}

function automationFromPressure(
  pressurePoints: SourcedClaim[],
  _locale: 'en' | 'nb',
): AiAutomationAngle | null {
  const pressure = pressurePoints[0]
  if (!pressure) return null
  const emergency = hasEmergencySiteSignal(pressure.claim)
  return {
    issue: pressure.claim,
    example: emergency
      ? 'route after-hours callouts so the scheduled jobs stay on the board.'
      : 'draft the repeated step in that public workflow before a person touches it.',
    invite: 'If that is not the pressure, steer me.',
    sourceUrl: pressure.sourceUrl,
  }
}

/** Legacy icebreaker template. Chapter facts stay on the card; the mouth is variant A. */
export function formatIcebreakerLine(input: {
  name?: string | null
  companyName?: string | null
  pack: GroundedPackV1
  locale?: string | null
}): string {
  return formatResearchAckOpening({
    name: input.name,
    companyName: input.companyName || input.pack.company.name,
    locale: input.locale,
    sourceCount: spokenSourceCount(
      [
        ...input.pack.sources.map(source => source.url),
        ...input.pack.pressurePoints.map(point => point.sourceUrl),
        ...input.pack.roleSignals.map(signal => signal.sourceUrl),
      ],
      input.pack.company.domain,
    ),
  })
}

export function formatPackOpenWithoutBottleneck(input: {
  name?: string | null
  companyName?: string | null
  locale?: string | null
}): string {
  return formatResearchAckOpening({
    name: input.name,
    companyName: input.companyName,
    locale: input.locale,
  })
}

export function formatIcebreakerDirective(line: string): string {
  return (
    `${FIRST_TURN_HYPOTHESIS_OPENING}\n` +
    `FIRST CONSULTING LINE (verbatim):\n"""\n${line}\n"""\n` +
    `${ICEBREAKER_RULE}\n` +
    `${CONSULTANT_VOICE_RULE}\n` +
    `Never say "${GREETING_HEDGE_MARKER}". Never say "${GENERIC_LIVE_IMPROV_MARKER}".`
  )
}

export function formatGroundedPackSeed(pack: GroundedPackV1): string {
  const lines = [
    'GROUNDED PACK v1',
    GROUNDED_PACK_ANSWER_RULE,
    `Person: ${pack.person.fullName}` +
      (pack.person.role ? ` · ${pack.person.role}` : '') +
      (pack.person.profileUrl ? ` · ${pack.person.profileUrl}` : ''),
    `Company (email-domain lock): ${pack.company.name} · ${pack.company.domain}` +
      (pack.company.website ? ` · ${pack.company.website}` : '') +
      (pack.company.orgNumber ? ` · org ${pack.company.orgNumber}` : ''),
  ]
  if (pack.company.summary) lines.push(`Summary: ${clip(pack.company.summary, 400)}`)
  if (pack.roleSignals.length > 0) {
    lines.push('Role signals:')
    for (const signal of pack.roleSignals) {
      lines.push(`- ${signal.claim} (${signal.sourceUrl})`)
    }
  }
  if (pack.competitors.length > 0) {
    lines.push('Competitors (sourced):')
    for (const row of pack.competitors) lines.push(`- ${row.claim} (${row.sourceUrl})`)
  }
  if (pack.pressurePoints.length > 0) {
    lines.push('Pressure (sourced, not a personal bottleneck):')
    for (const row of pack.pressurePoints) lines.push(`- ${row.claim} (${row.sourceUrl})`)
  }
  if (pack.aiAutomationAngle) {
    lines.push(
      `AI automation: ${pack.aiAutomationAngle.issue} Example: ${pack.aiAutomationAngle.example} Invite: ${pack.aiAutomationAngle.invite}`,
    )
  }
  if (pack.sources.length > 0) {
    lines.push('Sources:')
    for (const source of pack.sources.slice(0, 8)) {
      const when = source.publishedAt ? ` published ${source.publishedAt}` : ''
      lines.push(`- ${source.title || source.url}: ${source.url}${when} retrieved ${source.retrievedAt}`)
    }
  }
  return lines.join('\n')
}
